// The settings item that arranges the phone's Dock (`/me/settings/phone-dock`),
// kept on this device.
//
// Promise:
// - One row per place: a handle, its icon and name (the workspace's places say
//   so), and whether it is shown. Dragging the handle with a finger or the
//   mouse moves the row (HTML drag does not work with touch); ↑ and ↓ on the
//   handle move it by one. A row taken past its neighbours lands where it is
//   let go; a cancelled drag moves nothing.
// - A line after as many shown rows as this screen's Dock holds says where
//   the Dock ends (the rest are in "⋯").
// - A hidden place is in neither the Dock nor the menu.
// - Handles and checkboxes take a finger (44 px) without looking bigger.
import { m } from "@trameria/i18n";
import {
  type PointerEvent as ReactPointerEvent,
  useRef,
  useState,
} from "react";
import type { SettingsItem } from "../app/screen-part";
import { desktopShell } from "../device/desktop-shell";
import { useDeviceSetting } from "../device/device-setting";
import { useShellMenu } from "../shell/outside/source";
import { Icon, iconOf } from "../ui/icon";
import {
  arrangeDock,
  DOCK_SIDE,
  type DockEntry,
  dockCapacity,
  dockEntries,
  dockPrefsSetting,
} from "./items";
import { useFrameWidth } from "./layout";

/** Moves `key` to `to` among the keys shown, leaving the others where they are. */
export function moveKey(
  order: readonly string[],
  shown: readonly string[],
  key: string,
  to: number,
): string[] {
  const from = shown.indexOf(key);
  if (from < 0 || to === from) return [...order];
  const nextShown = shown.filter((k) => k !== key);
  nextShown.splice(Math.max(0, Math.min(nextShown.length, to)), 0, key);
  // The shown keys take their new order in the slots they held.
  const slots = new Set(shown);
  let i = 0;
  return order.map((k) => (slots.has(k) ? (nextShown[i++] ?? k) : k));
}

function DockArrangement() {
  const shell = useShellMenu();
  const place = shell.place;
  const { me, place: theirs } = dockEntries(shell);
  const [prefs, setPrefs] = useDeviceSetting(dockPrefsSetting);
  const viewport = useFrameWidth();
  const both = me.length > 0 && theirs.length > 0;
  // The Dock of a phone this wide, with nothing beside it.
  const capacity = dockCapacity(Math.min(viewport, 600) - 2 * DOCK_SIDE, both);
  const { order } = arrangeDock(me, theirs, prefs, capacity);
  const byKey = new Map([...me, ...theirs].map((e) => [e.key, e]));
  const rows = order
    .map((k) => byKey.get(k))
    .filter((e): e is DockEntry => e !== undefined);
  const hidden = new Set(prefs.hidden);
  const keys = rows.map((r) => r.key);
  // Where the Dock ends: after the `capacity`-th shown row.
  let shownCount = 0;
  let cutAfter: string | null = null;
  for (const r of rows) {
    if (hidden.has(r.key)) continue;
    shownCount++;
    if (shownCount === capacity) cutAfter = r.key;
  }
  const save = (next: Partial<{ order: string[]; hidden: string[] }>) =>
    setPrefs({
      order: next.order ?? order,
      hidden: next.hidden ?? [...hidden],
    });
  const move = (key: string, to: number) =>
    save({ order: moveKey(order, keys, key, to) });

  const list = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ key: string; dy: number } | null>(null);
  const start = useRef<{ y: number; key: string } | null>(null);
  const indexAt = (y: number, key: string) => {
    const els = [
      ...(list.current?.querySelectorAll<HTMLElement>("[data-dock-row]") ?? []),
    ];
    // The row being dragged follows the finger: measure the others.
    const others = els.filter((el) => el.dataset.dockRow !== key);
    let at = 0;
    for (const el of others) {
      const r = el.getBoundingClientRect();
      if (y > r.top + r.height / 2) at++;
    }
    return at;
  };
  const onDown = (e: ReactPointerEvent, key: string) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    start.current = { y: e.clientY, key };
    setDrag({ key, dy: 0 });
  };
  const onMove = (e: ReactPointerEvent) => {
    const s = start.current;
    if (!s) return;
    setDrag({ key: s.key, dy: e.clientY - s.y });
  };
  const onUp = (e: ReactPointerEvent) => {
    const s = start.current;
    start.current = null;
    setDrag(null);
    if (s) move(s.key, indexAt(e.clientY, s.key));
  };
  const onCancel = () => {
    start.current = null;
    setDrag(null);
  };

  return (
    <div className="mt-4">
      <p className="text-[12px] leading-[1.6] text-faint">
        {m.phone_dock_settings_note()}
      </p>
      <div
        ref={list}
        className="mt-3 rounded-[10px] border border-panel-border"
      >
        {rows.map((r, i) => {
          const off = hidden.has(r.key);
          const dragging = drag?.key === r.key;
          return (
            <div key={r.key}>
              <div
                data-dock-row={r.key}
                className={`flex h-11 items-center gap-3 px-2 ${
                  dragging
                    ? "relative z-10 rounded-[8px] bg-surface shadow-[var(--shadow-popover)]"
                    : ""
                }`}
                style={
                  dragging
                    ? { transform: `translateY(${drag.dy}px)` }
                    : undefined
                }
              >
                <button
                  type="button"
                  aria-label={m.phone_dock_settings_move({ label: r.label })}
                  onPointerDown={(e) => onDown(e, r.key)}
                  onPointerMove={onMove}
                  onPointerUp={onUp}
                  onPointerCancel={onCancel}
                  onLostPointerCapture={() => start.current && onCancel()}
                  onKeyDown={(e) => {
                    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
                    e.preventDefault();
                    const to = i + (e.key === "ArrowUp" ? -1 : 1);
                    if (to < 0 || to >= rows.length) return;
                    move(r.key, to);
                  }}
                  // 44 px for a finger, drawn as the icon's size.
                  className="-my-[6px] -ml-1 flex size-11 flex-none cursor-grab touch-none items-center justify-center text-faint"
                >
                  <Icon kind="updown" size={16} />
                </button>
                <r.icon className="size-4 flex-none text-muted" />
                <span
                  className={`min-w-0 flex-1 truncate text-[13px] ${off ? "text-faint" : "text-fg"}`}
                >
                  {r.label}
                </span>
                {r.group === "place" && place && (
                  <span className="max-w-[40%] flex-none truncate text-[11.5px] text-faint">
                    {place.name}
                  </span>
                )}
                <label className="-my-[6px] -mr-1 flex size-11 flex-none cursor-pointer items-center justify-center">
                  <input
                    type="checkbox"
                    checked={!off}
                    aria-label={m.phone_dock_settings_show({ label: r.label })}
                    onChange={(e) =>
                      save({
                        hidden: e.target.checked
                          ? [...hidden].filter((k) => k !== r.key)
                          : [...hidden, r.key],
                      })
                    }
                  />
                </label>
              </div>
              {cutAfter === r.key && i < rows.length - 1 && (
                <div
                  data-dock-cutoff=""
                  className="flex items-center gap-2 px-3 py-1 text-[11px] text-faint"
                >
                  <span className="h-px flex-1 bg-border" />
                  {m.phone_dock_settings_cutoff()}
                  <span className="h-px flex-1 bg-border" />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {!place && (
        <p className="mt-3 text-[12px] text-faint">
          {m.phone_dock_settings_no_place()}
        </p>
      )}
    </div>
  );
}

export const phoneDockSettings: SettingsItem = {
  id: "phone-dock",
  order: 25,
  label: () => m.phone_dock_settings(),
  icon: iconOf("phone"),
  description: () => m.phone_dock_settings_description(),
  // The desktop app is never a phone.
  useShown: () => desktopShell() === null,
  Component: DockArrangement,
};
